import { Box, Flex, Grid, GridItem, Wrap, WrapItem } from '@chakra-ui/react';
import { Button } from '@main/complykit/components/button';
import { IconButton } from '@main/complykit/components/icon-button';
import { Tooltip } from '@main/complykit/components/tooltip';
import { Table } from '@tanstack/react-table';
import { ComponentType, SVGProps } from 'react';

import { isTableHeaderAction, TableHeaderAction } from '../../actions/types';
import {
  AddFilter,
  ColumnFilter,
  GlobalFilter,
  hasColumnFilters,
  hasGlobalFilter,
} from '../filters';
import { AddSort, ColumnSort, shouldDisplaySort } from '../sort';
import { TableHeaderElement } from '../table';
import { SaveConfiguration } from './save-configuration';
import { TableSettings } from './table-settings';

export function TableMenu<TData>({
  table,
  hasColumnOrderChanged,
  hasColumnVisibilityChanged,
  hasColumnSizingChanged,
  tableName,
  settingsNamespace,
  tableId,
  isColumnsLoading,
  enableSavingFiltersAndSort,
  headerActions,
}: {
  table: Table<TData>;
  hasColumnOrderChanged: boolean;
  hasColumnVisibilityChanged: boolean;
  hasColumnSizingChanged: boolean;
  tableName: string;
  settingsNamespace?: string;
  tableId?: string;
  isColumnsLoading: boolean;
  enableSavingFiltersAndSort: boolean;
  headerActions?: TableHeaderElement[];
}) {
  const { columnFilters, sorting } = table.getState();
  const columns = table.getAllColumns();
  const isSavingFiltersAndSortEnabled = enableSavingFiltersAndSort;

  return (
    <Grid p={3} alignItems="center" templateColumns="auto 320px" gap={6}>
      <GridItem>
        <Wrap overflow="visible">
          {hasColumnFilters(table) && (
            <>
              {columnFilters
                .filter(({ id }) => columns.find((column) => column.id === id))
                .map(({ id }) => (
                  <WrapItem key={id}>
                    <ColumnFilter table={table} columnId={id} />
                  </WrapItem>
                ))}
              <WrapItem>
                <AddFilter table={table} />
              </WrapItem>
            </>
          )}
          {shouldDisplaySort(table) && (
            <>
              {sorting
                .filter(({ id }) => columns.find((column) => column.id === id))
                .map(({ id }) => (
                  <WrapItem key={id}>
                    <ColumnSort table={table} columnId={id} />
                  </WrapItem>
                ))}
              <WrapItem>
                <AddSort table={table} />
              </WrapItem>
            </>
          )}
          <WrapItem>
            {isSavingFiltersAndSortEnabled && (
              <SaveConfiguration
                table={table}
                tableName={tableName}
                settingsNamespace={settingsNamespace}
                tableId={tableId}
                isColumnsLoading={isColumnsLoading}
              />
            )}
          </WrapItem>
        </Wrap>
      </GridItem>
      <GridItem borderColor="green.400" as={Flex} justifyContent="end" gap={3}>
        {hasGlobalFilter(table) && (
          <Box w="xs">
            <GlobalFilter table={table} />
          </Box>
        )}

        {(headerActions?.length ?? 0) > 0 && (
          <Flex gap={2}>
            {headerActions?.map((action) =>
              isTableHeaderAction(action) ? (
                <ButtonAction
                  key={'tooltip' in action ? action.tooltip : action.label}
                  {...action}
                />
              ) : (
                action
              ),
            )}
          </Flex>
        )}

        <TableSettings
          table={table}
          hasColumnOrderChanged={hasColumnOrderChanged}
          hasColumnVisibilityChanged={hasColumnVisibilityChanged}
          hasColumnSizingChanged={hasColumnSizingChanged}
        />
      </GridItem>
    </Grid>
  );
}

const ButtonAction = (props: TableHeaderAction) => {
  const { tooltip, isDisabled, disabledTooltip, isLoading, onClick } = props;
  const label = 'label' in props ? props.label : undefined;
  const icon = 'icon' in props ? props.icon : undefined;

  const onPress = () => void onClick();

  /* The documented swap: the action's tooltip, or the disabled reason. A
   * labelled Button without a configured tooltip stays silent (it wears its
   * name); the types guarantee a disabled action carries its reason. */
  const content = isDisabled ? disabledTooltip : tooltip;

  return (
    <Tooltip content={content ?? ''} isDisabled={!content}>
      {icon ? (
        <IconButton
          variant="outline"
          icon={icon as ComponentType<SVGProps<SVGSVGElement>>}
          // empty string to keep typescript happy - types ensure tooltip is always defined,
          // typescript just can't infer the narrow type here
          label={tooltip ?? ''}
          isDisabled={isDisabled}
          isPending={isLoading}
          onPress={onPress}
        />
      ) : (
        <Button variant="outline" isDisabled={isDisabled} isPending={isLoading} onPress={onPress}>
          {label}
        </Button>
      )}
    </Tooltip>
  );
};
